import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addMember,
  changeMemberRole,
  listMembers,
  removeMember,
  replaceMemberRolePresets,
  type AuthorizationModel,
  type Member,
  type MemberRole,
  type RolePreset,
} from "../api/members";
import Select from "../components/Select";
import { useSession } from "../auth/useSession";
import { canManageMembers } from "../lib/role";

const MEMBERS_QUERY_KEY = ["members"] as const;

const BUTTON_PRIMARY =
  "inline-flex h-9 items-center gap-1.5 rounded-md bg-slate-900 px-3 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-default disabled:opacity-50";
const BUTTON_SECONDARY =
  "inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-default disabled:opacity-50";

const INPUT =
  "h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 shadow-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200";

type RolePresetOption = {
  value: RolePreset;
  label: string;
  description: string;
};

const ROLE_PRESET_OPTIONS: readonly RolePresetOption[] = [
  {
    value: "viewer",
    label: "Nur lesen",
    description: "Kann freigegebene Mandantendaten ansehen.",
  },
  {
    value: "purchasing",
    label: "Einkauf",
    description:
      "Legt Warenkörbe an, bestellt, prüft Auftragsbestätigungen und lädt Belege hoch.",
  },
  {
    value: "controlling",
    label: "Controlling",
    description:
      "Prüft Belege und Preise, ordnet Gutschriften zu, verwaltet Kostenstellen und lädt Belege hoch.",
  },
  {
    value: "administrator",
    label: "Administrator",
    description: "Verwaltet Benutzer, Importe, Quellen und Datenqualität.",
  },
] as const;

const ROLE_PRESET_ORDER = new Map(
  ROLE_PRESET_OPTIONS.map((option, index) => [option.value, index]),
);

export default function MembersPage() {
  const { user } = useSession();

  if (!canManageMembers(user)) {
    return (
      <main className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="text-xl font-semibold text-slate-900">Benutzerverwaltung</h1>
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Sie haben keinen Zugriff auf die Benutzer- und Rollenverwaltung.
          Wenden Sie sich an eine zuständige Administratorin oder einen
          zuständigen Administrator Ihres Mandanten.
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Benutzerverwaltung</h1>
        <p className="mt-1 text-sm text-slate-500">
          Verwalten Sie Zugriffe und Rollen für den Mandanten{" "}
          <span className="font-medium text-slate-700">{user?.customer_id}</span>.
        </p>
      </header>
      <MembersWorkspace />
    </main>
  );
}

function MembersWorkspace() {
  const query = useQuery({
    queryKey: MEMBERS_QUERY_KEY,
    queryFn: listMembers,
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return (
      <section aria-busy="true">
        <p className="text-sm text-slate-500">Lade Benutzer …</p>
      </section>
    );
  }
  if (query.isError) {
    return (
      <p role="alert" className="text-sm text-rose-600">
        {(query.error as Error).message}
      </p>
    );
  }

  const data = query.data;
  if (!data) {
    return (
      <p role="alert" className="text-sm text-rose-600">
        Das Rollenmodell konnte nicht geladen werden.
      </p>
    );
  }

  return (
    <>
      <GrantAccessForm authorizationModel={data.authorization_model} />
      <section aria-labelledby="members-with-access-title">
        <h2
          id="members-with-access-title"
          className="mb-2 text-sm font-semibold text-slate-900"
        >
          Benutzer mit Zugriff
        </h2>
        <MembersTable
          authorizationModel={data.authorization_model}
          members={data.items}
        />
      </section>
    </>
  );
}

function GrantAccessForm({
  authorizationModel,
}: {
  authorizationModel: AuthorizationModel;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("member");
  const [rolePresets, setRolePresets] = useState<RolePreset[]>(["viewer"]);

  const mutation = useMutation({
    mutationFn: () =>
      addMember(
        email.trim(),
        authorizationModel === "legacy" ? role : "member",
        authorizationModel === "v2" ? rolePresets : undefined,
      ),
    onSuccess: () => {
      setEmail("");
      setRole("member");
      setRolePresets(["viewer"]);
      void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
    },
  });

  const isV2 = authorizationModel === "v2";

  return (
    <section className="mb-8 rounded-md border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-slate-900">
        Zugriff für bestehendes Konto vergeben
      </h2>
      <p className="mt-1 text-xs text-slate-500">
        Diese Funktion gilt nur für bereits registrierte TruuCost-Konten. Geben
        Sie die vollständige E-Mail-Adresse ein und wählen Sie{" "}
        {isV2 ? "die Rollenbereiche" : "die Rolle"}. Es wird kein Konto erstellt
        und keine Einladung versendet. Rollen bereits berechtigter Benutzer
        verwalten Sie in der Liste.
      </p>
      <form
        className="mt-3 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim()) mutation.mutate();
        }}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex-1">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              E-Mail
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="kollegin@firma.at"
              className={INPUT}
            />
          </label>
          {!isV2 ? (
            <label className="sm:w-40">
              <span className="mb-1 block text-xs font-medium text-slate-600">
                Rolle
              </span>
              <Select
                containerClassName="block"
                value={role}
                onChange={(event) => setRole(event.target.value as MemberRole)}
              >
                <option value="member">Mitglied</option>
                <option value="admin">Admin</option>
              </Select>
            </label>
          ) : null}
          {!isV2 ? (
            <button
              type="submit"
              className={BUTTON_PRIMARY}
              disabled={mutation.isPending || !email.trim()}
            >
              {mutation.isPending
                ? "Zugriff wird vergeben …"
                : "Zugriff vergeben"}
            </button>
          ) : null}
        </div>
        {isV2 ? (
          <>
            <RolePresetEditor
              legend="Rollenbereiche"
              value={rolePresets}
              onChange={setRolePresets}
              disabled={mutation.isPending}
            />
            <div className="flex justify-end">
              <button
                type="submit"
                className={BUTTON_PRIMARY}
                disabled={mutation.isPending || !email.trim()}
              >
                {mutation.isPending
                  ? "Zugriff wird vergeben …"
                  : "Zugriff vergeben"}
              </button>
            </div>
          </>
        ) : null}
      </form>
      {mutation.isError && (
        <p role="alert" className="mt-3 text-sm text-rose-600">
          {(mutation.error as Error).message}
        </p>
      )}
    </section>
  );
}

function MembersTable({
  authorizationModel,
  members,
}: {
  authorizationModel: AuthorizationModel;
  members: Member[];
}) {
  const isV2 = authorizationModel === "v2";

  return (
    <section className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
      <table className={isV2 ? "w-full min-w-[52rem] text-sm" : "w-full text-sm"}>
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2 font-medium">Benutzer</th>
            <th className="px-4 py-2 font-medium">
              {isV2 ? "Rollenbereiche" : "Rolle"}
            </th>
            <th className="px-4 py-2 font-medium text-right">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <MemberRow
              key={member.user_id}
              member={member}
              authorizationModel={authorizationModel}
            />
          ))}
        </tbody>
      </table>
      {members.length === 0 && (
        <p className="px-4 py-6 text-sm text-slate-500">
          Keine Benutzer mit Zugriff.
        </p>
      )}
    </section>
  );
}

function MemberRow({
  member,
  authorizationModel,
}: {
  member: Member;
  authorizationModel: AuthorizationModel;
}) {
  const queryClient = useQueryClient();

  const roleMutation = useMutation({
    mutationFn: (role: MemberRole) => changeMemberRole(member.user_id, role),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY }),
  });
  const removeMutation = useMutation({
    mutationFn: () => removeMember(member.user_id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY }),
  });
  const presetMutation = useMutation({
    mutationFn: (rolePresets: RolePreset[]) =>
      replaceMemberRolePresets(member.user_id, rolePresets),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY }),
  });

  const busy =
    roleMutation.isPending ||
    removeMutation.isPending ||
    presetMutation.isPending;
  const error =
    roleMutation.error ?? removeMutation.error ?? presetMutation.error;
  const isV2 = authorizationModel === "v2";

  return (
    <tr className="border-b border-slate-100 align-top last:border-0">
      <td className="px-4 py-3">
        <div className="font-medium text-slate-900">
          {member.display_name || member.email || member.user_id}
          {member.is_self && (
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500">
              Eigenes Konto
            </span>
          )}
        </div>
        {member.email && member.display_name && (
          <div className="text-xs text-slate-500">{member.email}</div>
        )}
        {error && (
          <div role="alert" className="mt-1 text-xs text-rose-600">
            {(error as Error).message}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {isV2 ? (
          member.is_self ? (
            <RolePresetSummary value={member.role_presets} />
          ) : (
            <MemberRolePresetEditor
              member={member}
              disabled={busy}
              saveError={presetMutation.error}
              onSave={(value) => presetMutation.mutate(value)}
            />
          )
        ) : member.is_self ? (
          <span className="text-slate-700">
            {member.role === "admin" ? "Admin" : "Mitglied"}
          </span>
        ) : (
          <Select
            size="sm"
            value={member.role}
            disabled={busy}
            onChange={(event) =>
              roleMutation.mutate(event.target.value as MemberRole)
            }
          >
            <option value="member">Mitglied</option>
            <option value="admin">Admin</option>
          </Select>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {!member.is_self && (
          <button
            type="button"
            className={BUTTON_SECONDARY}
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  `Mitglied ${member.email || member.user_id} entfernen?`,
                )
              ) {
                removeMutation.mutate();
              }
            }}
          >
            {removeMutation.isPending ? "Entferne …" : "Entfernen"}
          </button>
        )}
      </td>
    </tr>
  );
}

function MemberRolePresetEditor({
  member,
  disabled,
  saveError,
  onSave,
}: {
  member: Member;
  disabled: boolean;
  saveError: Error | null;
  onSave: (value: RolePreset[]) => void;
}) {
  const original = normalizeRolePresets(member.role_presets);
  const [draft, setDraft] = useState<RolePreset[]>(original);

  useEffect(() => {
    setDraft(original);
  }, [member.role_presets, saveError]);

  const changed = !sameRolePresets(draft, original);

  return (
    <div className="min-w-[27rem]">
      <RolePresetEditor
        legend={`Rollenbereiche für ${
          member.display_name || member.email || member.user_id
        }`}
        value={draft}
        onChange={setDraft}
        disabled={disabled}
        compact
      />
      {changed ? (
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            className={BUTTON_PRIMARY}
            disabled={disabled}
            onClick={() => onSave(draft)}
          >
            {disabled ? "Speichert …" : "Rollen speichern"}
          </button>
          <button
            type="button"
            className={BUTTON_SECONDARY}
            disabled={disabled}
            onClick={() => setDraft(original)}
          >
            Verwerfen
          </button>
        </div>
      ) : null}
    </div>
  );
}

function RolePresetEditor({
  legend,
  value,
  onChange,
  disabled,
  compact = false,
}: {
  legend: string;
  value: RolePreset[];
  onChange: (value: RolePreset[]) => void;
  disabled: boolean;
  compact?: boolean;
}) {
  const normalized = normalizeRolePresets(value);

  return (
    <fieldset>
      <legend
        className={
          compact ? "sr-only" : "mb-1 text-xs font-medium text-slate-600"
        }
      >
        {legend}
      </legend>
      <div
        className={
          compact
            ? "grid grid-cols-2 gap-x-4 gap-y-2 xl:grid-cols-3"
            : "grid gap-2 sm:grid-cols-2 lg:grid-cols-5"
        }
      >
        {ROLE_PRESET_OPTIONS.map((option) => {
          const checked = normalized.includes(option.value);
          return (
            <label
              key={option.value}
              className={[
                "flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-2 text-left",
                checked
                  ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                  : "border-slate-200 bg-white text-slate-700",
                disabled ? "cursor-default opacity-60" : "hover:bg-slate-50",
              ].join(" ")}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                aria-label={option.label}
                onChange={() =>
                  onChange(toggleRolePreset(normalized, option.value))
                }
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
              />
              <span>
                <span className="block text-xs font-semibold">{option.label}</span>
                {!compact ? (
                  <span className="mt-0.5 block text-[11px] leading-4 text-slate-500">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {!compact ? (
        <p className="mt-2 text-xs text-slate-500">
          Administrator ist ein zusätzlicher Bereich und enthält keine
          Fachrolle. Nur lesen steht immer allein.
        </p>
      ) : null}
    </fieldset>
  );
}

function RolePresetSummary({ value }: { value: RolePreset[] }) {
  const labels = normalizeRolePresets(value).map(
    (preset) =>
      ROLE_PRESET_OPTIONS.find((option) => option.value === preset)?.label ??
      preset,
  );
  return (
    <div className="flex flex-wrap gap-1.5">
      {labels.map((label) => (
        <span
          key={label}
          className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function toggleRolePreset(
  current: RolePreset[],
  selected: RolePreset,
): RolePreset[] {
  if (selected === "viewer") return ["viewer"];
  const withoutViewer = current.filter((preset) => preset !== "viewer");
  if (withoutViewer.includes(selected)) {
    const next = withoutViewer.filter((preset) => preset !== selected);
    return next.length > 0 ? normalizeRolePresets(next) : ["viewer"];
  }
  return normalizeRolePresets([...withoutViewer, selected]);
}

function normalizeRolePresets(value: RolePreset[]): RolePreset[] {
  const unique = [...new Set(value)];
  if (unique.length === 0 || unique.includes("viewer")) return ["viewer"];
  return unique.sort(
    (left, right) =>
      (ROLE_PRESET_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER) -
      (ROLE_PRESET_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER),
  );
}

function sameRolePresets(left: RolePreset[], right: RolePreset[]): boolean {
  return (
    normalizeRolePresets(left).join("|") ===
    normalizeRolePresets(right).join("|")
  );
}
